import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { useApi } from '../hooks/useApi';

const parseNewsDate = (dateInput) => {
  if (typeof dateInput === 'number') {
    return new Date(dateInput * 1000);
  }

  if (typeof dateInput === 'string' && /^\d+$/.test(dateInput)) {
    return new Date(Number(dateInput) * 1000);
  }

  return new Date(dateInput);
};

const formatArticleTime = (dateInput) => {
  const date = parseNewsDate(dateInput);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "Just now";

  const hours = seconds / 3600;
  if (hours >= 48) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (hours >= 1) return Math.floor(hours) + "h ago";

  const minutes = seconds / 60;
  if (minutes >= 1) return Math.floor(minutes) + "m ago";

  return Math.floor(seconds) + "s ago";
};

const ArticleSkeleton = () => (
  <div className="bg-gray-800/40 backdrop-blur-md border border-gray-700/50 rounded-2xl overflow-hidden animate-pulse h-full">
    <div className="h-48 bg-gray-700/50" />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-gray-700/50 rounded w-1/4" />
      <div className="h-6 bg-gray-700/50 rounded w-full" />
      <div className="h-10 bg-gray-700/50 rounded w-full" />
    </div>
  </div>
);

const Articles = () => {
  const { data: newsData, loading: newsLoading, error: newsError, fetchData: fetchNews } = useApi('/api/news');
  const { data: sentimentData } = useApi('/api/sentiment');

  const articles = newsData?.data || [];
  const outlookText = sentimentData?.outlook || "Market data is loading. Sentiment analysis will update once fresh snapshots are available.";
  const outlookUpdatedAt = sentimentData?.updated_at || null;

  useEffect(() => {
    const refreshId = window.setInterval(() => {
      fetchNews('/api/news');
    }, 10 * 60 * 1000);

    return () => window.clearInterval(refreshId);
  }, [fetchNews]);

  return (
    <>
      <Helmet>
        <title>Crypto News & Market Analysis | CryptoBag</title>
        <meta name="description" content="Latest cryptocurrency news, market analysis, and insights on Bitcoin, Ethereum, and the broader crypto ecosystem." />
        <link rel="canonical" href="https://cryptobag.net/articles" />
      </Helmet>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fadeIn">
        <header className="mb-12">
          <h1 className="text-4xl font-extrabold text-white tracking-tight mb-6">
            Latest Crypto News <span className="text-indigo-500">&amp; Market Analysis</span>
          </h1>

          <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 backdrop-blur-md p-6 rounded-2xl shadow-2xl">
            <div className="flex items-center mb-3">
              <span className="flex h-3 w-3 mr-3">
                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
              </span>
              <h3 className="text-indigo-300 font-bold uppercase tracking-widest text-xs">CryptoBag Analysis</h3>
            </div>
            <p className="text-gray-200 text-lg leading-relaxed">
              "{outlookText}"
            </p>
            {outlookUpdatedAt && (
              <p className="text-xs uppercase tracking-widest text-indigo-200/70 mt-4">
                Updated {new Date(outlookUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
          </div>
        </header>

        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white">Latest Headlines</h2>
        </div>

        {newsError && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-200 p-4 rounded-xl mb-8 text-center">
            {newsError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {newsLoading ? (
            Array(6).fill(0).map((_, i) => <ArticleSkeleton key={i} />)
          ) : (
            articles.map((article, idx) => (
              <a
                key={idx}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gray-800/30 backdrop-blur-sm border border-gray-700/30 rounded-2xl overflow-hidden flex flex-col h-full transition-all duration-300 hover:bg-gray-800/50 hover:border-indigo-500/50 hover:-translate-y-2 shadow-xl"
              >
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={article.thumb_2x || article.thumb}
                    alt={article.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Crypto+News'; }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-indigo-600/90 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                      {article.news_site}
                    </span>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center text-xs text-gray-500 mb-3 space-x-2">
                    <span>{formatArticleTime(article.updated_at)}</span>
                    <span>|</span>
                    <span className="text-indigo-400 font-medium">Trending</span>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-indigo-400 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-gray-400 text-sm line-clamp-3 mb-6">
                    {article.description || "Read the full report for in-depth coverage."}
                  </p>
                  <div className="mt-auto flex items-center text-indigo-400 text-sm font-semibold">
                    Read Story -&gt;
                  </div>
                </div>
              </a>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Articles;
